// One line of the agent's reasoning. Done steps recede (muted), the running
// step is emphasised, planned steps sit quiet until their turn.

import { type FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import {
  formatDuration,
  formatElapsed,
  friendlyLabel,
  softenedLabel,
  SHOW_ELAPSED_AT_S,
} from './stepLabels';
import { StatusDot } from '../../../components/blocks/primitives';
import type { TimelineStep } from './StepTimeline.types';

// Done steps recede, the running one is emphasised, planned ones sit quiet.
const textClass = (status: TimelineStep['status']): string => {
  const base = 'text-[12px] truncate';
  if (status === 'error') return `${base} text-error-700`;
  if (status === 'active') return `${base}  text-secondary-700`;
  if (status === 'pending') return `${base} text-secondary-400`;
  return `${base} italic text-secondary-500`;
};

export const StepRow: FC<{
  step: TimelineStep;
  /** Seconds active so far. Only meaningful when status === 'active'. */
  elapsedSec?: number;
}> = ({ step, elapsedSec }) => {
  const isActive = step.status === 'active';
  const base = friendlyLabel(step);
  const label = isActive
    ? softenedLabel(base, elapsedSec ?? 0, !step.id.startsWith('action:'))
    : base;
  const showElapsed = isActive && (elapsedSec ?? 0) >= SHOW_ELAPSED_AT_S;
  const showDuration =
    !isActive && typeof step.durationMs === 'number' && step.durationMs > 0;

  return (
    <Flex align="center" gap="10px" py="3px">
      <Box display="inline-flex" pl="2px" flexShrink={0}>
        <StatusDot status={step.status} />
      </Box>
      <Text variant="small" className={textClass(step.status)} title={label}>
        {label}
      </Text>
      {showElapsed && (
        <Text
          variant="small"
          className="text-[12px] text-secondary-400 flex-shrink-0"
        >
          {formatElapsed(elapsedSec ?? 0)}
        </Text>
      )}
      {showDuration && (
        <Text
          variant="small"
          className="text-[12px] text-secondary-400 flex-shrink-0"
        >
          {formatDuration(step.durationMs ?? 0)}
        </Text>
      )}
    </Flex>
  );
};
